/**
 * Brand + vertical derivation for analytics attribution.
 *
 * One GA4 property ("WinIt Webapp Prod (PayNyc/TraficViolation)") receives every
 * brand domain this bundle serves, so campaign reports mix PayNYC parking traffic
 * with traffic-violation traffic and cannot be split. These two dimensions are the
 * split: `brand` says which white-label domain the visit came in on, `vertical`
 * says which funnel the page belongs to.
 *
 * Every brand domain this project actually serves is single-vertical today, by
 * construction -- see the blockHosts() redirects in next.config.js. So the GA4
 * built-in `hostName` dimension alone would separate them, and `vertical` is not
 * currently doing work that hostname cannot. Both are kept anyway because:
 *
 *   - one brand spans two hosts (trafficviolation.info AND trafficviolations.info),
 *     which hostName splits into two rows and `brand` does not;
 *   - `brand` survives a domain being added, renamed or pointed elsewhere, where
 *     every saved hostName report would silently need editing;
 *   - `vertical` is what keeps the split correct the first time a host serves more
 *     than one funnel, which is a routing change rather than a tracking change.
 */

export const BRANDS = {
  paynyc: 'paynyc',
  trafficviolations: 'trafficviolations',
  divorcelookup: 'divorcelookup',
  winit: 'winit',
}

export const VERTICALS = {
  parking: 'parking',
  traffic: 'traffic',
  divorce: 'divorce',
  other: 'other',
}

// Matches the apex and any subdomain, the same shape the host-gated redirects in
// next.config.js use. Anything unrecognised (previews, *.vercel.app, localhost)
// falls through to `winit` rather than inventing a bucket per preview URL.
const BRAND_PATTERNS = [
  [/(^|\.)paynyctickets\.com$/, BRANDS.paynyc],
  [/(^|\.)trafficviolations?\.info$/, BRANDS.trafficviolations],
  [/(^|\.)divorcelookup\.com$/, BRANDS.divorcelookup],
]

// Leading locale segment: routes are served as both /parking/pay and
// /en/parking/pay, so the vertical lives at index 0 or 1.
const LOCALE_SEGMENTS = ['en', 'es']

const VERTICAL_BY_SEGMENT = {
  parking: VERTICALS.parking,
  traffic: VERTICALS.traffic,
  divorce: VERTICALS.divorce,
}

export const brandFromHost = hostname => {
  if (!hostname) return BRANDS.winit
  const host = String(hostname).toLowerCase()
  const match = BRAND_PATTERNS.find(([pattern]) => pattern.test(host))
  return match ? match[1] : BRANDS.winit
}

export const verticalFromPath = pathname => {
  if (!pathname) return VERTICALS.other
  const segments = String(pathname).split('/').filter(Boolean)
  const first = segments[0]
  const segment = LOCALE_SEGMENTS.includes(first) ? segments[1] : first
  return VERTICAL_BY_SEGMENT[segment] || VERTICALS.other
}

/** Brand + vertical for the page currently rendered. SSR-safe: `{}` on the server. */
export const currentBrandContext = () => {
  if (typeof window === 'undefined' || !window.location) return {}
  return {
    brand: brandFromHost(window.location.hostname),
    vertical: verticalFromPath(window.location.pathname),
  }
}
